import type { MouseEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface PatientLinkProps {
    patientId: string | null;
    // Passed as router state so PatientFiles can show a name immediately,
    // before its own fetch resolves (same convention as Home.tsx / DetailPanel.tsx).
    patientName?: string | null;
    // Query string for the patient page, e.g. '?tab=monitoring'.
    search?: string;
    children: ReactNode;
    className?: string;
}

const DEFAULT_LINK_CLASS = 'hover:underline hover:text-gray-900 transition-colors';

// Wraps existing patient-name markup with a link to PatientFiles, only when
// a patientId is available. Renders children unwrapped otherwise, so the
// caller's current display (including "-" fallbacks) is unaffected.
export function PatientLink({
    patientId,
    patientName,
    search,
    children,
    className
}: PatientLinkProps) {
    if (!patientId) return <>{children}</>;

    const handleClick = (e: MouseEvent) => {
        // Rows this renders inside (charges/invoices tables) already open a
        // detail drawer on click; don't let that fire too.
        e.stopPropagation();
    };

    return (
        <Link
            to={{ pathname: `/patients/${patientId}`, search }}
            state={patientName ? { patientName } : undefined}
            onClick={handleClick}
            className={className ?? DEFAULT_LINK_CLASS}
        >
            {children}
        </Link>
    );
}
